import process from 'process';
import io, { Socket } from 'socket.io-client';

import BootOptionModel from '@common/models/BootOption';
import Sequence from '@common/Sequence';
import conf from '@common/conf';
import define from '@common/define';
import { isValidKey } from '@common/utils';
import ChModel from '@common/models/Ch';
import { normalizeTuneSubscribeOption, tuneSubscribeOptionInit } from '@common/talkn-config';
import type { TuneSubscribeOption } from '@common/talkn-config';
import WsClientToApiRequestActions from '@api-client/redux/actions/apiToServerRequest';
import type ApiState from '@api-client/state';
import buildSubscriptionPlan from '@api-client/subscription/buildSubscriptionPlan';

import WssWorker, { Pid, TuneId, statusTunning } from '.';
import type { CommandMethod, CommandRequestMap } from './StoresDispatcher';

const isDocker = Boolean(process.env.IS_DOCKER);

function getLocationHref(): string {
  if (typeof location !== 'undefined' && typeof location.href === 'string') {
    return location.href;
  }
  return '';
}

// 複数のioのリクエストとレスポンスを受け取るのに専念する
export default class ToServer {
  ios: { [tuneId: TuneId]: Socket };
  methods: { [key: string]: Function };
  wssWorker: WssWorker;
  // リスナー二重登録防止用の追跡Set
  private _registeredEmitListeners = new Set<string>();
  private _registeredBroadcastListeners = new Set<string>();
  // Blob URL workers (SecurityError fallback) have hostname="" but href="blob:http://localhost:PORT/..."
  private static isLocalhostLocation(loc: { hostname: string; href: string }): boolean {
    if (loc.hostname === 'localhost' || loc.hostname === '127.0.0.1') return true;
    return /^blob:https?:\/\/(localhost|127\.0\.0\.1)[:/]/.test(loc.href);
  }

  static get domain() {
    // WebWorker has no `window` (IS_BROWSER=false), so conf.env falls back to
    // DEVELOPMENT via getHostName()='localhost'. Resolve location explicitly.
    const loc =
      typeof window !== 'undefined' ? window.location
      : typeof self !== 'undefined' ? (self as unknown as { location: Location }).location
      : null;
    if (loc) {
      return ToServer.isLocalhostLocation(loc)
        ? define.DEVELOPMENT_DOMAIN
        : define.PRODUCTION_API_DOMAIN;
    }
    // Node.js server context
    return conf.env === define.DEVELOPMENT || conf.env === define.LOCALHOST
      ? define.DEVELOPMENT_DOMAIN
      : define.PRODUCTION_API_DOMAIN;
  }
  static get option() {
    return {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      //transports: ['polling', 'websocket'],
      //upgrade: true,
      transports: ['websocket'],
      upgrade: false,
      forceNew: false,
      query: {},
      withCredentials: false,
      extraHeaders: {},
    };
  }
  constructor(wssWorker: WssWorker) {
    this.tune = this.tune.bind(this);
    this.untune = this.untune.bind(this);
    this.exe = this.exe.bind(this);
    this.onResponseEmit = this.onResponseEmit.bind(this);
    this.onResponseBroadcast = this.onResponseBroadcast.bind(this);
    this.offResponse = this.offResponse.bind(this);

    this.ios = {};
    this.methods = {};
    this.wssWorker = wssWorker;
  }

  // 有効なメソッド一覧
  private readonly validMethods = new Set(['tune', 'untune']);

  public exe(
    pid: Pid | undefined,
    tuneId: TuneId,
    method: string,
    params: Partial<ApiState> | CommandRequestMap[CommandMethod]
  ): void {
    // tune/untune: 接続管理メソッドとして実行（socket.emitは不要）
    if (this.validMethods.has(method) && isValidKey(method, this)) {
      const methodFn = this[method as keyof this];
      if (typeof methodFn === 'function') {
        (methodFn as (tuneId: TuneId, params: Partial<ApiState>, pid?: Pid) => void).call(
          this,
          tuneId,
          params as Partial<ApiState>,
          pid
        );
      }
      return; // tune/untuneは接続/切断で処理完了、socket.emitは送信しない
    }

    // その他のメソッド: Socket.IOイベントとして送信
    if (this.ios[tuneId]) {
      const payload = pid ? { ...params, pid } : params;
      this.ios[tuneId].emit(method, payload);
    }
  }

  private tune(tuneId: TuneId, { bootOption }: Partial<ApiState>, pid?: Pid): void {
    let connection = ChModel.rootConnection;
    let tuneOption: TuneSubscribeOption = { ...tuneSubscribeOptionInit };
    let rankScope: string | undefined;
    let urlSearchParams = `?tuneId=${tuneId}`;
    let socketProtocol = Sequence.HTTPS_PROTOCOL;
    let hostPort = `${ToServer.domain}:${define.PORTS.HTTPS}`;

    if (bootOption) {
      connection = BootOptionModel.getConnection(bootOption.connection);
      const tuneRequest = BootOptionModel.getTuneRequest(bootOption);
      tuneOption = tuneRequest.tuneOption;
      rankScope = tuneRequest.rankScope;
      if (bootOption.host) {
        hostPort = bootOption.host;
      }
      if (bootOption.protocol) {
        socketProtocol = bootOption.protocol;
      }
      urlSearchParams += `&${BootOptionModel.getTuneOptionString(tuneOption)}`;
      if (rankScope) {
        urlSearchParams += `&rankScope=${encodeURIComponent(rankScope)}`;
      }
      if (bootOption.isObserver) {
        urlSearchParams += `&isObserver=1`;
      }
    } else {
      console.warn('[ToServer.tune] bootOption is undefined!');
    }

    const loc = typeof window !== 'undefined' ? window.location
      : typeof self !== 'undefined' ? (self as unknown as { location: Location }).location
      : null;
    const isBrowserLocalhost = loc !== null && ToServer.isLocalhostLocation(loc);
    if ((!bootOption?.host || bootOption.host === '') && isBrowserLocalhost) {
      hostPort = 'localhost:10444';
    }
    if ((!bootOption?.protocol || bootOption.protocol === '') && isBrowserLocalhost) {
      socketProtocol = Sequence.HTTPS_PROTOCOL;
    }

    const subscriptionPlan = buildSubscriptionPlan(tuneOption);

    // 同じtuneIdで再tuneする場合、古い接続を先にクローズしてからlive countを正確に1にする
    if (this.ios[tuneId]) {
      this.ios[tuneId].removeAllListeners();
      this.ios[tuneId].disconnect();
      delete this.ios[tuneId];
      this._registeredEmitListeners.delete(tuneId);
      Array.from(this._registeredBroadcastListeners)
        .filter((key) => key.startsWith(`${tuneId}:`))
        .forEach((key) => this._registeredBroadcastListeners.delete(key));
    }
    const uniqueTuneId = tuneId;

    const endpoint = `${socketProtocol}//${hostPort}${urlSearchParams}`;
    const connectOption: any = { ...ToServer.option, forceNew: true };

    // APIキーが設定されている場合、Socket.IO authで送信
    if (bootOption?.apiKey) {
      connectOption.auth = { apiKey: bootOption.apiKey };
    }

    this.ios[uniqueTuneId] = io(endpoint, connectOption);

    this.ios[uniqueTuneId].on('connect', () => {
      // サーバー側のtuneハンドラをトリガーしてレスポンスを受け取る
      this.ios[uniqueTuneId].emit('tune', { pid });
    });

    this.ios[uniqueTuneId].on('disconnect', (reason) => {
      const response: Partial<ApiState> & { type: string } = { type: 'untune' };
      this.wssWorker.postMessage({
        pid: pid,
        tuneId: uniqueTuneId,
        method: response.type,
        apiState: response,
        serverToApiType: Sequence.SERVER_TO_API_EMIT,
      });

      // クリーンアップ: イベントリスナー削除と参照削除
      if (this.ios[uniqueTuneId]) {
        this.ios[uniqueTuneId].removeAllListeners();
        delete this.ios[uniqueTuneId];
      }
    });

    this.ios[uniqueTuneId].on('connect_error', (error) => {
      // 全 connect_error をメインスレッドへ伝播し、tune Promise を即時 reject させる
      // （旧実装では timeout/refused のみ untune を呼び、auth エラー等は黙殺されて
      // postMessage の 30 秒タイムアウトに到達していた）
      const errorMessage = error?.message || 'connect_error';
      const errorPayload: Partial<ApiState> & { type: string; error: string } = {
        type: 'tune',
        error: errorMessage,
      };
      this.wssWorker.postMessage({
        pid,
        tuneId: uniqueTuneId,
        method: 'tune',
        apiState: errorPayload,
        serverToApiType: Sequence.SERVER_TO_API_EMIT,
      });
      this.untune(uniqueTuneId, {}, pid);
    });

    this.onResponseEmit(uniqueTuneId, connection);
    this.onResponseBroadcast(uniqueTuneId, connection, subscriptionPlan.streams);
  }

  private untune(tuneId: TuneId, _params?: Partial<ApiState>, pid?: Pid): void {
    if (isValidKey(tuneId, this.ios)) {
      const socket = this.ios[tuneId];

      // すべてのイベントリスナーを削除
      socket.removeAllListeners();

      // Socket.IO接続を切断
      socket.disconnect();

      // 参照を削除してガベージコレクション対象にする
      delete this.ios[tuneId];

      // リスナー追跡Setからも削除
      this._registeredEmitListeners.delete(tuneId);
      // Broadcast用は完全一致でなくprefixマッチで削除
      Array.from(this._registeredBroadcastListeners)
        .filter((key) => key.startsWith(`${tuneId}:`))
        .forEach((key) => this._registeredBroadcastListeners.delete(key));
    }

    const response: Partial<ApiState> & { type: string } = { type: 'untune' };
    this.wssWorker.postMessage({
      pid: pid, // 元のPIDをそのまま使用
      tuneId,
      method: response.type,
      apiState: response,
      serverToApiType: Sequence.SERVER_TO_API_EMIT,
    });
  }

  private onResponseEmit(tuneId: string, connection: string) {
    const socket = this.ios[tuneId];
    if (!socket) return;

    // 二重登録防止：Setで追跡
    const key = `${tuneId}`;
    if (this._registeredEmitListeners.has(key)) {
      return;
    }

    socket.on(tuneId, (apiState: Partial<ApiState> & { type: string }) => {
      const method = apiState.type;
      const serverToApiType = Sequence.SERVER_TO_API_EMIT;
      // サーバーからのレスポンスにpidが含まれている場合はそれを使用
      const pid = (apiState as any).pid;
      this.wssWorker.postMessage({ pid, tuneId, method, serverToApiType, apiState });
    });

    this._registeredEmitListeners.add(key);
  }

  private onResponseBroadcast(tuneId: string, connection: string, streams: string[]) {
    const socket = this.ios[tuneId];
    if (!socket) return;

    const broadcastKey = `${tuneId}:${connection}`;
    if (this._registeredBroadcastListeners.has(broadcastKey)) {
      return;
    }

    const callback = (apiState: Partial<ApiState> & { type: string }) => {
      const method = apiState.type;
      const serverToApiType = Sequence.SERVER_TO_API_BROADCAST;
      const pid = undefined;
      this.wssWorker.postMessage({ pid, tuneId, method, serverToApiType, apiState });
    };

    socket.on(`tune:${connection}`, callback);
    socket.on(`untune:${connection}`, callback);
    socket.on(`typing:${connection}`, callback);

    streams.forEach((stream) => {
      const eventKey = `${stream}:${connection}`;
      socket.on(eventKey, callback);
    });

    this._registeredBroadcastListeners.add(broadcastKey);
  }

  private offResponse(tuneId: TuneId, connection: string) {
    const socket = this.ios[tuneId];
    if (socket) {
      // リスナーが存在する場合のみ削除
      if (socket.listeners(tuneId).length > 0) {
        socket.off(tuneId);
      }
      if (socket.listeners(connection).length > 0) {
        socket.off(connection);
      }
    }
  }
}
